import React, { useState } from "react";
import { Link } from "react-router-dom";
import { submitContact } from "../lib/api";
import WhatsNewModal from "./WhatsNewModal";

const TABS = [
  {
    id: "general",
    label: "GENERAL",
    icon: "fa-envelope-open-text",
    blurb: "Questions, feedback, bug reports — drop us a line.",
    subjectPlaceholder: "How can we help?",
  },
  {
    id: "gamedev",
    label: "GAME DEVS",
    icon: "fa-code-branch",
    blurb: "Building a game? Pitch a partnership, API access, or integration.",
    subjectPlaceholder: "Studio / game / integration idea",
  },
  {
    id: "advertise",
    label: "ADVERTISE",
    icon: "fa-bullhorn",
    blurb: "Want to put your brand in front of competitive players? Let's talk.",
    subjectPlaceholder: "Campaign type / budget range",
  },
];

const INBOX_EMAIL = "team@leaguehub.co";

export function ContactPage({ onToast }) {
  const [tab, setTab] = useState("general");
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    company: "",
    website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [ack, setAck] = useState({ delivered: false, acknowledged: false, sentEmail: "" });

  const current = TABS.find((t) => t.id === tab);
  const showOrgFields = tab !== "general";

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      onToast?.("Please fill in name, email, subject and message.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitContact({
        category: tab,
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        company: showOrgFields ? form.company.trim() || null : null,
        website: showOrgFields ? form.website.trim() || null : null,
      });
      setAck({
        delivered: Boolean(res?.delivered),
        acknowledged: Boolean(res?.acknowledged),
        sentEmail: form.email.trim(),
      });
      setSent(true);
      onToast?.(`📬 Message sent to ${INBOX_EMAIL}. We'll be in touch.`);
      setForm({ name: "", email: "", subject: "", message: "", company: "", website: "" });
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Could not send. Try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="page-z" data-testid="contact-page">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-10 pb-16">
        <Link
          to="/"
          data-testid="contact-back-home"
          className="inline-flex items-center gap-2 text-xs tracking-widest text-[#a3a3ac] hover:text-[#14b8a6]"
        >
          <i className="fas fa-arrow-left" /> BACK TO ROADMAP
        </Link>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl grad-text mt-4">📡 CONTACT</h1>
        <p className="text-[#a3a3ac] mt-3 max-w-3xl">
          Reach the LEAGUE HUB crew directly. All messages route to{" "}
          <a href={`mailto:${INBOX_EMAIL}`} className="text-[#14b8a6] underline" data-testid="contact-inbox-email">
            {INBOX_EMAIL}
          </a>
          .
        </p>

        <div className="mt-8 grid lg:grid-cols-[1fr_360px] gap-8 items-start">
          {/* LEFT — tabs + form */}
          <div className="glass rounded-3xl p-6 md:p-8">
            <div className="flex flex-wrap gap-2 mb-6" data-testid="contact-tabs">
              {TABS.map((t) => {
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => { setTab(t.id); setSent(false); }}
                    data-testid={`contact-tab-${t.id}`}
                    className={`px-4 py-2 rounded-2xl text-xs tracking-widest border transition-colors ${
                      active
                        ? "bg-[#14b8a6]/15 border-[#14b8a6] text-[#14b8a6]"
                        : "bg-black/40 border-white/15 text-white/70 hover:text-white"
                    }`}
                  >
                    <i className={`fas ${t.icon} mr-2`} /> {t.label}
                  </button>
                );
              })}
            </div>

            <p className="text-sm text-white/70 mb-5" data-testid="contact-blurb">{current.blurb}</p>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Your name *">
                  <input
                    value={form.name}
                    onChange={onChange("name")}
                    data-testid="contact-name"
                    placeholder="Ada Operator"
                    className="contact-input"
                    required
                    maxLength={120}
                  />
                </Field>
                <Field label="Reply email *">
                  <input
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    data-testid="contact-email"
                    placeholder="you@studio.gg"
                    className="contact-input"
                    required
                  />
                </Field>
              </div>

              {showOrgFields && (
                <div className="grid md:grid-cols-2 gap-4" data-testid="contact-org-fields">
                  <Field label={tab === "gamedev" ? "Studio / company" : "Brand / company"}>
                    <input
                      value={form.company}
                      onChange={onChange("company")}
                      data-testid="contact-company"
                      placeholder={tab === "gamedev" ? "Neon Studios" : "Your brand"}
                      className="contact-input"
                      maxLength={160}
                    />
                  </Field>
                  <Field label="Website (optional)">
                    <input
                      value={form.website}
                      onChange={onChange("website")}
                      data-testid="contact-website"
                      placeholder="https://"
                      className="contact-input"
                      maxLength={240}
                    />
                  </Field>
                </div>
              )}

              <Field label="Subject *">
                <input
                  value={form.subject}
                  onChange={onChange("subject")}
                  data-testid="contact-subject"
                  placeholder={current.subjectPlaceholder}
                  className="contact-input"
                  required
                  maxLength={160}
                />
              </Field>

              <Field label="Message *">
                <textarea
                  value={form.message}
                  onChange={onChange("message")}
                  data-testid="contact-message"
                  rows={6}
                  placeholder="Tell us what's on your mind..."
                  className="contact-input resize-y"
                  required
                  maxLength={5000}
                />
              </Field>

              <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
                <p className="text-[10px] tracking-widest text-white/40">
                  PRIVACY · We only use your email to reply to this message.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="btn-neon rounded-2xl px-6 py-3 text-sm tracking-widest disabled:opacity-60"
                >
                  {submitting ? "SENDING..." : sent ? "SEND ANOTHER" : `SEND TO ${INBOX_EMAIL.toUpperCase()}`}
                </button>
              </div>
            </form>

            {sent && (
              <div
                className="mt-5 rounded-2xl border border-[#14b8a6]/40 bg-[#14b8a6]/10 p-4 text-sm text-[#14b8a6]"
                data-testid="contact-success"
              >
                <div className="flex items-start gap-3">
                  <i className="fas fa-circle-check mt-0.5" />
                  <div className="space-y-2 leading-relaxed">
                    <p className="font-bold">Got it! Your message is in the queue.</p>
                    {ack.acknowledged ? (
                      <p data-testid="contact-ack-inbox">
                        <i className="fas fa-envelope-open-text mr-1" />
                        Check <strong>{ack.sentEmail}</strong> — we just sent you a copy.
                        If it&apos;s not in your inbox in a minute or two, peek in your
                        Promotions or Spam folder.
                      </p>
                    ) : (
                      <p data-testid="contact-ack-pending" className="text-[#14b8a6]/80">
                        We&apos;ll be in touch at <strong>{ack.sentEmail}</strong> within
                        1&ndash;2 business days.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — what's new + disclaimer */}
          <aside className="space-y-6">
            <div className="glass rounded-3xl p-6" data-testid="contact-whats-new">
              <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold">✨ WHAT'S NEW</p>
              <h3 className="font-display text-2xl grad-text mt-2">FRESH PATCH</h3>
              <p className="text-sm text-white/70 mt-3">
                See the latest features, fixes, and what's queued for the next sprint.
              </p>
              <button
                onClick={() => setWhatsNewOpen(true)}
                data-testid="contact-open-whats-new"
                className="mt-4 inline-flex items-center gap-2 text-xs tracking-widest text-[#14b8a6] border border-[#14b8a6]/40 rounded-2xl px-4 py-2 hover:bg-[#14b8a6]/10"
              >
                <i className="fas fa-sparkles" /> OPEN CHANGELOG
              </button>
            </div>

            <div
              className="rounded-3xl border border-[#14b8a6]0/30 bg-[#14b8a6]0/5 p-6 text-sm text-[#14b8a6]/80"
              data-testid="contact-disclaimer"
            >
              <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold">
                <i className="fas fa-triangle-exclamation mr-2" /> DISCLAIMER
              </p>
              <p className="mt-3 leading-relaxed">
                LEAGUE HUB is provided <strong>as-is</strong>. Use the site, leagues, matchmaking,
                team challenges, and direct messages <strong>at your own risk</strong>. We are not
                responsible for user-generated content, third-party links (Discord, game services),
                scheduling conflicts, prize disputes, or losses arising from competitive play.
              </p>
              <p className="mt-3 leading-relaxed">
                We do not verify the identity, age, or skill of operators. Treat opponents with
                respect, never share payment or personal credentials, and report abuse via this
                contact form.
              </p>
              <p className="mt-3 leading-relaxed text-[#14b8a6]/60 text-xs">
                By using this site you agree to play fairly, follow each game's terms of service,
                and accept that LEAGUE HUB may suspend accounts that break community rules.
              </p>
            </div>
          </aside>
        </div>
      </div>

      <WhatsNewModal open={whatsNewOpen} onClose={() => setWhatsNewOpen(false)} />

      <style>{`
        .contact-input {
          width: 100%;
          background: rgba(0, 0, 0, 0.55);
          border: 1px solid rgba(20, 184, 166, 0.25);
          color: #fff;
          padding: 0.75rem 1rem;
          border-radius: 1rem;
          font-size: 0.875rem;
          transition: border-color 0.15s ease;
        }
        .contact-input:focus {
          outline: none;
          border-color: #14b8a6;
          box-shadow: 0 0 0 2px rgba(20, 184, 166,0.15);
        }
        .contact-input::placeholder { color: rgba(255,255,255,0.35); }
      `}</style>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[3px] text-[#a3a3ac] mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export default ContactPage;
